import ShimmerImage from 'core-lib/components/ShimmerImage';
import * as React from 'react';
import Icon from 'ui-v2/components/Icon/Icon';
import { Loader } from 'ui-v2/components/Loader';
import { trackCtaClickedStoreV2 } from '../../../utils/events';
import s from './VideoCard.module.scss';

interface VideoCardProps {
  url: string;
  thumbnailUrl: string;
  customStyle?: React.CSSProperties;
  showControls?: boolean;
  isActive?: boolean;
  onVideoEnd?: () => void;
}
let timer;
export const VideoCard = ({
  url,
  thumbnailUrl,
  customStyle,
  showControls = true,
  isActive = false,
  onVideoEnd = () => {},
}: VideoCardProps) => {
  const [videoState, setVideoState] = React.useState({
    playing: false,
    isMuted: true,
    showControls: showControls,
    isBuffering: true,
    isReady: true,
    duration: 0,
    showVideo: false,
    played: 0,
  });
  // ref
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const mediaRef = React.useRef();
  const handleMuteUnmute = () => {
    setVideoState({ ...videoState, isMuted: !videoState.isMuted });
  };
  const handlePlayPause = e => {
    if (e?.stopPropagation) e.stopPropagation();
    setVideoState({ ...videoState, playing: !videoState.playing });
    if (videoState.playing) {
      // @ts-ignore
      videoRef.current.pause();
      trackCtaClickedStoreV2('pdp_video', 'pause', {
        product_id: window?.location?.pathname?.split('/').pop(),
      });
    } else {
      // @ts-ignore
      videoRef.current.play();
      trackCtaClickedStoreV2('pdp_video', 'play', {
        product_id: window?.location?.pathname?.split('/').pop(),
      });
    }
  };

  const handleVideoClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    if (!videoState.showControls) {
      setVideoState({ ...videoState, showControls: !videoState.showControls });
      return;
    }
  };
  React.useEffect(() => {
    if (videoState?.playing) {
      timer = setTimeout(() => {
        setVideoState(prev => ({
          ...prev,
          showControls: false,
        }));
      }, 1500);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [videoState.showControls, videoState.playing]);

  React.useEffect(() => {
    if (!isActive && videoState?.playing) {
      videoRef.current.pause();
      setVideoState(prev => ({
        ...prev,
        playing: false,
        showControls: true,
      }));
    }
  }, [isActive, videoState?.playing]);
  return (
    <div className={s.video_container} ref={mediaRef}>
      <div
        id="react_player"
        className={s.player}
        onClick={e => {
          if (showControls) {
            handleVideoClick(e);
          }
        }}
        aria-hidden
        style={customStyle}
      >
        <video
          className={s.react_player}
          controls={false}
          playsInline
          width="100%"
          height="100%"
          muted={videoState.isMuted}
          autoPlay
          onTimeUpdate={e => {
            // @ts-ignore
            let val = e.target.currentTime;
            setVideoState(prev => ({
              ...prev,
              duration: val,
            }));
          }}
          onPlaying={e =>
            setVideoState(prev => ({
              ...prev,
              isBuffering: false,
              playing: true,
              // @ts-ignore
              duration: e.target.duration,
            }))
          }
          onLoadStart={() => {
            setVideoState({
              ...videoState,
              isBuffering: true,
              playing: false,
            });
          }}
          onWaiting={e =>
            setVideoState({
              ...videoState,
              isBuffering: true,
              playing: false,
            })
          }
          onEnded={() => {
            setVideoState({
              ...videoState,
              playing: false,
              played: 1,
              showControls: true,
            });
            onVideoEnd();
          }}
          onClick={() => setVideoState({ ...videoState, showControls: true })}
          ref={videoRef}
        >
          <source src={url} type="video/webm" />
          <source src={url} type="video/mp4" />
          <source src={url} type="video/ogg" />
        </video>
        {videoState?.isBuffering && videoState.duration > 0 && !videoState?.playing && (
          <div className={s.loader}>
            <Loader width={64} />
          </div>
        )}
      </div>
      {videoState.isBuffering &&
        videoState.duration === 0 &&
        !videoState?.playing &&
        !!thumbnailUrl && (
          <div className={s.thumbnail}>
            <ShimmerImage src={thumbnailUrl} loading="eager" className={s.image} />
          </div>
        )}
      {videoState.showControls && (
        <div className={s.on_screen_controls}>
          <button onClick={handlePlayPause} type="button" className={s.play}>
            {videoState.playing ? (
              <Icon name="PauseBold" color="on_dark_bg" width={30} height={30} />
            ) : (
              <Icon name="PlayBold" color="on_dark_bg" width={30} height={30} />
            )}
          </button>
        </div>
      )}
      {showControls && (
        <button className={s.mute} type="button" onClick={handleMuteUnmute}>
          {videoState.isMuted ? (
            <Icon name="VolumeOffRegular" color="on_dark_bg" />
          ) : (
            <Icon name="VolumeRegular" color="on_dark_bg" />
          )}
        </button>
      )}
    </div>
  );
};
